import { Link } from "@tanstack/react-router";
import type { SafetyVideoStatus } from "@unbox/api";
import { Announcement03, ArrowUpRight, Trash01, VideoRecorder, XCircle } from "@untitledui/icons";
import { Camera, MoreVertical, Play } from "lucide-react";
import { useMemo, useState } from "react";
import { useRemoveVideoFromAllCampaigns, useVideoCampaignsBatchStatus } from "@/api/video-campaigns";
import { AddToCampaignMenuItem } from "@/components/assurance/add-to-campaign-menu-item";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { Badge } from "@/components/base/badges/badges";
import { RemoveCreatorFromCollabModal } from "@/components/remove-creator-from-collab-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMerchant } from "@/providers/MerchantProvider";
import { error, success } from "@/providers/ToastProvider";
import { videoExplorerRoute } from "@/router";
import { getCreatorName } from "@/utils/creator-name";
import { formatDate } from "@/utils/date-conversion";
import { ManageCampaignsModal } from "./manage-campaigns-modal";

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const formatVideoDate = (date: Date) => {
  const formattedDate = formatDate(date);
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${formattedDate} @${time}`;
};

type VideoItem = {
  videoId: string;
  creatorId: string | null;
  urlFirstFrame?: string | null;
  videoUrl?: string | null;
  videoTitle: string;
  publishDate: string;
  gmv: number;
  unitSales: number;
  views: number;
  gpm: number;
  likes?: number;
  flagCount: number;
  flagTypes: string[];
  safetyStatus: SafetyVideoStatus;
};

type VideosTabProps = {
  data: {
    creatorId: string;
    creatorName: string;
    nickname?: string | null;
    thumbnail?: string | null;
    targetCollaborations?: {
      collaborationId: string;
      name: string;
      status: string | null;
      endTime?: string | null;
      type?: string | null;
      kind?: "target" | "open" | "invitation" | null;
      membershipStatus?: string | null;
    }[];
    videos: VideoItem[];
  };
  currencyFormatter: Intl.NumberFormat;
};

export const VideosTab = ({ data, currencyFormatter }: VideosTabProps) => {
  const removeFromAllCampaigns = useRemoveVideoFromAllCampaigns();

  const videoIds = useMemo(() => data.videos.map((v) => v.videoId), [data.videos]);
  const { data: batchStatus } = useVideoCampaignsBatchStatus(videoIds);

  const [manageCampaignsVideoId, setManageCampaignsVideoId] = useState<string | null>(null);
  const [showRemoveCollabModal, setShowRemoveCollabModal] = useState(false);

  const merchant = useMerchant();
  const creatorNames = getCreatorName(data.nickname, data.creatorName);
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <VideoRecorder className="w-4 h-4 text-tertiary" />
        <h3 className="text-sm font-medium text-secondary">Recent brand content</h3>
      </div>

      {/* Videos List */}
      <div className="space-y-4">
        {data.videos.length === 0 && (
          <div className="italic h-24 w-full flex items-center text-sm justify-center text-tertiary">
            No Related Videos
          </div>
        )}
        {data.videos.map((video) => {
          const videoDate = new Date(video.publishDate);
          const adStatus = batchStatus?.[video.videoId];
          const isInAd = adStatus?.isInAdCampaign === true;
          const campaignCount = adStatus?.campaignCount ?? 0;

          return (
            <div
              key={video.videoId}
              className="border border-border-primary rounded-lg hover:border-gray-300 transition-colors relative"
            >
              <div className="flex min-h-44 rounded-lg overflow-hidden">
                {/* Video Thumbnail */}
                <div className="relative w-38 flex-shrink-0 bg-black self-stretch overflow-hidden">
                  {video.urlFirstFrame ? (
                    <>
                      <img
                        src={video.urlFirstFrame}
                        alt={video.videoTitle}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Play icon overlay */}
                      <Link
                        to={videoExplorerRoute.to}
                        params={{ merchantId: merchant }}
                        search={{ video_id: video.videoId }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded p-1.5 hover:bg-white transition-colors z-10"
                      >
                        <Play className="w-4 h-4 text-gray-700 fill-gray-700" />
                      </Link>
                      {isInAd && (
                        <div className="absolute bottom-2 left-2 px-1.5 py-0.5 flex items-center gap-0.5 rounded-full bg-utility-gray-blue-50 border-utility-gray-blue-200 backdrop-blur-sm z-10">
                          <Announcement03 className="h-2.5 w-2.5 text-utility-gray-blue-700" />
                          <span className="text-[10px] font-semibold leading-none text-utility-gray-blue-700">Ad</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="flex-1 flex flex-col min-w-0 justify-between">
                  {/* Section 1: Info and Menu */}
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Link
                        to={videoExplorerRoute.to}
                        params={{ merchantId: merchant }}
                        search={{ video_id: video.videoId }}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary underline decoration-dotted hover:text-primary/80 text-left truncate flex-1 min-w-0"
                      >
                        {video.videoTitle}
                      </Link>
                      <DetailedVideoStatusBadge status={video.safetyStatus} showInfoIcon={true} />

                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="p-1.5 hover:bg-gray-100 rounded flex-shrink-0 transition-colors"
                            aria-label="Video actions"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" sideOffset={5} className="w-[240px]">
                          <DropdownMenuItem asChild>
                            <Link
                              to={videoExplorerRoute.to}
                              params={{ merchantId: merchant }}
                              search={{ video_id: video.videoId }}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer flex gap-2"
                            >
                              <VideoRecorder className="w-4 h-4 text-tertiary" />
                              See Video in Detail
                            </Link>
                          </DropdownMenuItem>

                          {isInAd && (
                            <>
                              <DropdownMenuSeparator />
                              {campaignCount === 1 && (
                                <DropdownMenuItem
                                  className="cursor-pointer flex gap-2"
                                  onClick={() =>
                                    removeFromAllCampaigns.mutate(
                                      { videoId: video.videoId },
                                      {
                                        onSuccess: () => success("Video successfully removed from campaign."),
                                        onError: () => error("Failed to remove video from campaign."),
                                      },
                                    )
                                  }
                                >
                                  <Trash01 className="w-4 h-4 text-tertiary" />
                                  Remove From Ads Campaign
                                </DropdownMenuItem>
                              )}
                              {campaignCount > 1 && (
                                <DropdownMenuItem
                                  className="cursor-pointer flex gap-2"
                                  onClick={() => setManageCampaignsVideoId(video.videoId)}
                                >
                                  <Trash01 className="w-4 h-4 text-tertiary" />
                                  Remove From Ads Campaign
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          {adStatus && !isInAd && !adStatus.isExcludedFromAdCampaign && !adStatus.isQuarantined && (
                            <AddToCampaignMenuItem withSeparator />
                          )}

                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="cursor-pointer flex gap-2"
                            onClick={() => setShowRemoveCollabModal(true)}
                          >
                            <XCircle className="w-4 h-4 text-tertiary" />
                            Remove creator from collab
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <p className="text-xs text-tertiary mb-2">{formatVideoDate(videoDate)}</p>

                    {video.flagCount > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs text-tertiary font-medium">Risks Identified</p>
                        <div className="flex flex-wrap gap-1.5">
                          {video.flagTypes.map((risk, idx) => (
                            <Badge key={`${video.videoId}-${risk}-${idx}`} color="gray" size="sm">
                              {risk}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section 2: Stats */}
                  <div>
                    <div className="border-t border-border-primary" />
                    <div className="grid grid-cols-4 gap-4 p-3">
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">GMV</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {currencyFormatter.format(video.gmv)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Views</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {numFormatter.format(video.views)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Likes</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {video.likes ? numFormatter.format(video.likes) : "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-tertiary mb-1 text-center">Sales</div>
                        <div className="text-sm font-medium text-primary text-center">
                          {numFormatter.format(video.unitSales)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* See All Link */}
      {data.videos.length > 0 && data.videos[0].creatorId && (
        <Link
          to={videoExplorerRoute.to}
          params={{ merchantId: merchant }}
          search={{
            creator: [
              {
                id: data.videos[0].creatorId,
                label: creatorNames.nickname ?? creatorNames.handle ?? data.creatorName,
              },
            ],
          }}
          className="flex items-center gap-1 text-sm text-secondary hover:text-primary font-medium transition-colors"
        >
          See all content from this creator
          <ArrowUpRight className="w-4 h-4" />
        </Link>
      )}

      {/* Manage Campaigns Modal */}
      {manageCampaignsVideoId && (
        <ManageCampaignsModal
          videoId={manageCampaignsVideoId}
          open={!!manageCampaignsVideoId}
          onOpenChange={(open) => {
            if (!open) setManageCampaignsVideoId(null);
          }}
        />
      )}

      {/* Remove from Collaboration Modal */}
      <RemoveCreatorFromCollabModal
        creator={data}
        open={showRemoveCollabModal}
        onOpenChange={setShowRemoveCollabModal}
      />
    </div>
  );
};
